/* ============================================================
   DATA ARTIKEL - Berita & Artikel Kesehatan

   Letakkan di: app/edukasi-kesehatan/artikel.js

   Halaman artikel berada di /edukasi-kesehatan/<slug>, jadi kartu di
   ArticleCarousel.jsx pada beranda - yang sudah menunjuk ke
   ARTICLE_BASE = "/edukasi-kesehatan" - langsung terhubung tanpa
   diubah.

   ============================================================
   >>> ISI ARTIKEL HARUS DATANG DARI MATERI RESMI RS <<<

   Saya TIDAK bisa membaca isi berkas PNG leaflet, flyer, dan postermu.
   Yang bisa saya baca hanya bagian yang kebetulan terbaca di tangkapan
   layar yang kamu kirim. Karena itu:

   - Artikel "hemodialisa" di bawah isinya DISALIN dari leaflet
     Hemodialisa milikmu sendiri, bagian yang terbaca jelas. Tidak ada
     satu pun fakta medis yang saya tambahkan dari luar.
   - Artikel berikutnya harus kamu ketik ulang dari materinya, atau
     kirimkan teksnya ke saya dan saya yang menyusun strukturnya.

   JANGAN meminta saya mengarang isi medis dari pengetahuan umum.
   Untuk situs rumah sakit, isi yang tidak sama dengan materi yang
   sudah disetujui dokter bukan sekadar tidak rapi - ia berisiko, dan
   RS yang menanggungnya.

   ⚠️ `ditinjau` WAJIB DIISI SEBELUM TAYANG. Selama kosong, halaman
   menampilkan spanduk peringatan yang mencolok. Isi dengan nama dan
   gelar dokter yang meninjau, misalnya "dr. Andi Wijaya, Sp.PD".

   ============================================================
   BENTUK SATU ARTIKEL

   slug        alamatnya: /edukasi-kesehatan/<slug>. Huruf kecil,
               tanpa spasi. JANGAN diubah setelah tayang - tautan yang
               sudah dibagikan akan mati.
   tanggal     "2026-07-28". Dipakai untuk <time> dan pengurutan.
   penulis     { id, en }
   ditinjau    { id, en } - nama dokter peninjau
   judul       { id, en }
   ringkas     { id, en } - satu-dua kalimat, tampil di bawah judul
                dan dipakai sebagai deskripsi untuk mesin pencari
   gambar      path sampul; gambarAlt { id, en }
   materi      id materi di data.js yang jadi sumbernya (boleh null)
   isi         daftar blok - lihat di bawah
   faq         [{ tanya: {id,en}, jawab: {id,en} }]
   referensi   [{ teks, url, diakses }]

   BLOK YANG TERSEDIA di `isi`:
     { tipe: "paragraf", teks: { id, en } }
     { tipe: "judul", teks: { id, en } }
     { tipe: "daftar", butir: [{ id, en }, ...] }        -> bullet
     { tipe: "daftarAngka", butir: [{ id, en }, ...] }   -> bernomor
     { tipe: "bacaJuga", teks: { id, en }, href: "/..." }

   Blok dirender berurutan. Menambah jenis blok baru berarti menambah
   satu cabang di ArtikelContent.jsx - tapi lima ini sudah menutupi
   hampir semua bentuk artikel kesehatan.
   ============================================================ */

export const ARTIKEL = [
  {
    slug: "hemodialisa-cuci-darah",
    tanggal: "2026-07-04",
    penulis: { id: "Tim RS Jakarta Medical Center", en: "Jakarta Medical Center Hospital Team" },

    /* ⚠️ KOSONG - isi dengan nama dokter peninjau sebelum tayang. */
    ditinjau: { id: "", en: "" },

    judul: {
      id: "Hemodialisa (Cuci Darah): Proses, Lama Tindakan, dan Pencegahan Gagal Ginjal",
      en: "Haemodialysis: The Process, How Long It Takes, and Preventing Kidney Failure",
    },
    ringkas: {
      id: "Bagaimana hemodialisa bekerja, berapa lama dan sesering apa dijalani, serta langkah menjaga fungsi ginjal sejak dini.",
      en: "How haemodialysis works, how long and how often it is done, and steps to protect kidney function early.",
    },

    gambar: "/images/FASILITAS/2.jpg",
    gambarAlt: {
      id: "Lengan pasien yang terhubung ke selang mesin hemodialisa selama tindakan cuci darah berlangsung.",
      en: "A patient's arm connected to haemodialysis tubing during a dialysis session.",
    },

    /* Menghubungkan artikel ke materi cetaknya di /edukasi-kesehatan.
       Isi dengan `id` dari MATERI di data.js, atau null. */
    materi: "hemodialisa",

    isi: [
      {
        tipe: "paragraf",
        teks: {
          id: "Hemodialisa, atau yang lebih dikenal sebagai cuci darah, adalah tindakan menyaring racun dan cairan berlebih dari darah menggunakan mesin. Tindakan ini dijalani pasien yang fungsi ginjalnya sudah tidak mampu melakukan penyaringan itu sendiri.",
          en: "Haemodialysis is a procedure that filters waste products and excess fluid from the blood using a machine. It is used by patients whose kidneys can no longer carry out that filtering on their own.",
        },
      },
      {
        tipe: "judul",
        teks: { id: "Proses Hemodialisa", en: "How Haemodialysis Works" },
      },
      {
        tipe: "daftarAngka",
        butir: [
          {
            id: "Darah dialirkan keluar tubuh melalui selang khusus",
            en: "Blood is drawn out of the body through a special tube",
          },
          {
            id: "Darah masuk ke mesin dialisis (dialyzer)",
            en: "The blood enters the dialysis machine (dialyser)",
          },
          {
            id: "Mesin menyaring racun dan cairan berlebih",
            en: "The machine filters out waste products and excess fluid",
          },
          {
            id: "Darah yang sudah bersih dikembalikan ke tubuh",
            en: "The cleaned blood is returned to the body",
          },
        ],
      },
      {
        tipe: "paragraf",
        teks: {
          id: "Satu kali tindakan berlangsung sekitar 4 sampai 5 jam, dan biasanya dijalani 2 sampai 3 kali per minggu. Jadwal pastinya ditentukan dokter sesuai kondisi masing-masing pasien.",
          en: "One session lasts around four to five hours, and is usually done two to three times a week. The exact schedule is set by the doctor according to each patient's condition.",
        },
      },
      {
        tipe: "judul",
        teks: {
          id: "Apakah Hemodialisa Menyembuhkan Gagal Ginjal?",
          en: "Does Haemodialysis Cure Kidney Failure?",
        },
      },
      {
        tipe: "paragraf",
        teks: {
          id: "Tidak. Hemodialisa tidak menyembuhkan gagal ginjal. Yang dilakukannya adalah menggantikan sebagian fungsi penyaringan ginjal, sehingga pasien tetap dapat hidup dan beraktivitas dengan lebih nyaman.",
          en: "No. Haemodialysis does not cure kidney failure. What it does is take over part of the kidneys' filtering function, so that patients can continue living and going about their activities more comfortably.",
        },
      },
      {
        tipe: "paragraf",
        teks: {
          id: "Selain hemodialisa, ada dua pilihan penanganan lain yang dapat didiskusikan dengan dokter:",
          en: "Besides haemodialysis, there are two other options that can be discussed with your doctor:",
        },
      },
      {
        tipe: "daftar",
        butir: [
          {
            id: "Dialisis peritoneal, yaitu cuci darah melalui rongga perut",
            en: "Peritoneal dialysis, which filters the blood through the abdominal cavity",
          },
          { id: "Transplantasi ginjal", en: "Kidney transplantation" },
        ],
      },
      {
        tipe: "bacaJuga",
        teks: {
          id: "CAPD (Continuous Ambulatory Peritoneal Dialysis)",
          en: "CAPD (Continuous Ambulatory Peritoneal Dialysis)",
        },
        href: "/edukasi-kesehatan",
      },
      {
        tipe: "judul",
        teks: { id: "Pencegahan Gagal Ginjal", en: "Preventing Kidney Failure" },
      },
      {
        tipe: "paragraf",
        teks: {
          id: "Sebagian besar kasus gagal ginjal berkembang perlahan dari kondisi yang sebenarnya bisa dikendalikan. Langkah berikut membantu menjaga fungsi ginjal:",
          en: "Most cases of kidney failure develop slowly from conditions that can actually be controlled. The following steps help protect kidney function:",
        },
      },
      {
        tipe: "daftar",
        butir: [
          {
            id: "Kontrol gula darah jika Anda memiliki diabetes",
            en: "Keep blood sugar under control if you have diabetes",
          },
          { id: "Kontrol tekanan darah", en: "Keep blood pressure under control" },
          { id: "Minum air putih cukup", en: "Drink enough water" },
          {
            id: "Hindari konsumsi obat tanpa pengawasan dokter",
            en: "Avoid taking medicines without a doctor's supervision",
          },
          { id: "Hindari rokok", en: "Avoid smoking" },
          {
            id: "Periksa fungsi ginjal secara berkala jika Anda berisiko",
            en: "Have your kidney function checked regularly if you are at risk",
          },
        ],
      },
    ],

    faq: [
      {
        tanya: {
          id: "Berapa lama satu kali hemodialisa?",
          en: "How long does one haemodialysis session take?",
        },
        jawab: {
          id: "Sekitar 4 sampai 5 jam per tindakan, umumnya 2 sampai 3 kali per minggu. Jadwalnya ditentukan dokter sesuai kondisi pasien.",
          en: "Around four to five hours per session, usually two to three times a week. The schedule is set by the doctor based on the patient's condition.",
        },
      },
      {
        tanya: {
          id: "Apakah pasien hemodialisa masih bisa beraktivitas seperti biasa?",
          en: "Can patients on haemodialysis still go about their normal activities?",
        },
        jawab: {
          id: "Hemodialisa membantu pasien tetap hidup dan beraktivitas dengan lebih nyaman. Sejauh mana aktivitas bisa dilanjutkan berbeda pada tiap pasien dan perlu didiskusikan dengan dokter yang menangani.",
          en: "Haemodialysis helps patients keep living and stay active more comfortably. How much activity is possible differs between patients and should be discussed with the treating doctor.",
        },
      },
    ],

    /* ⚠️ REFERENSI SENGAJA KOSONG.

       Leaflet sumbernya tidak mencantumkan rujukan, dan saya tidak
       menambahkan sendiri: mencantumkan sumber yang tidak benar-benar
       dipakai menyusun isinya adalah bentuk pemalsuan, sekecil apa pun
       niatnya.

       Kalau tim medis punya rujukan yang dipakai saat menyusun
       leafletnya, isi di sini dengan bentuk:
         { teks: "Cleveland Clinic. Hemodialysis.", url: "https://...",
           diakses: "4 Agustus 2026" }
       Selama kosong, bagian Referensi tidak dirender sama sekali. */
    referensi: [],
  },


  {
    slug: "kehamilan-ektopik-terganggu",
    tanggal: "2026-07-03",
    penulis: { id: "Tim RS Jakarta Medical Center", en: "Jakarta Medical Center Hospital Team" },
    ditinjau: { id: "", en: "" },
    judul: {
      id: "Kehamilan Ektopik Terganggu (KET): Tanda yang Tidak Boleh Ditunda",
      en: "Ruptured Ectopic Pregnancy: Warning Signs You Should Not Delay",
    },
    ringkas: {
      id: "Kehamilan ektopik terjadi ketika sel telur yang telah dibuahi berkembang di luar rahim. Kenali tandanya, karena kondisinya dapat memburuk dengan cepat.",
      en: "An ectopic pregnancy happens when a fertilised egg develops outside the womb. Know the signs, because the condition can worsen quickly.",
    },
    /* ============================================================
       SAMPUL ARTIKEL

       ATURAN PATH: buang "public", sisanya tulis apa adanya.
         public\images\FASILITAS\1.jpg  ->  "/images/FASILITAS/1.jpg"

       ⚠️ HURUF BESAR-KECIL HARUS PERSIS. Di Windows tidak dibedakan,
       jadi "/images/fasilitas/1.jpg" tetap jalan di komputermu. Tapi
       server tempat situs ini nanti tayang hampir selalu Linux, dan di
       sana dibedakan - gambarnya hilang begitu di-deploy padahal di
       lokal baik-baik saja. Ini salah satu bug yang paling sering baru
       ketahuan setelah tayang.

       gambarAlt di bawah ditulis dari foto yang terlihat di tangkapan
       layar: perempuan hamil sedang diperiksa dokter. Kalau fotonya
       nanti diganti, ganti juga kalimat ini - alt yang menggambarkan
       foto lain lebih menyesatkan daripada alt yang kosong.
       ============================================================ */
    gambar: "/images/FASILITAS/1.jpg",
    gambarAlt: {
      id: "Seorang perempuan hamil sedang diperiksa oleh dokter di ruang praktik.",
      en: "A pregnant woman being examined by a doctor in a consulting room.",
    },
    materi: "ket",
    isi: [
      {
        tipe: "paragraf",
        teks: {
          id: "Kehamilan ektopik adalah kondisi ketika sel telur yang telah dibuahi menempel dan berkembang di luar rahim. Karena tempatnya bukan tempat semestinya, kehamilan ini tidak dapat berkembang normal dan berisiko menimbulkan komplikasi.",
          en: "An ectopic pregnancy is when a fertilised egg implants and develops outside the womb. Because it is not in the place it should be, the pregnancy cannot develop normally and carries a risk of complications.",
        },
      },
      {
        tipe: "judul",
        teks: {
          id: "Kapan Harus Segera ke Fasilitas Kesehatan?",
          en: "When to Seek Medical Care Immediately",
        },
      },
      {
        tipe: "daftar",
        butir: [
          {
            id: "Tes kehamilan positif disertai nyeri perut bawah",
            en: "A positive pregnancy test together with lower abdominal pain",
          },
          {
            id: "Perdarahan tidak normal saat hamil muda",
            en: "Abnormal bleeding during early pregnancy",
          },
          {
            id: "Pusing atau hampir pingsan saat hamil muda",
            en: "Dizziness or near-fainting during early pregnancy",
          },
        ],
      },
      {
        tipe: "paragraf",
        teks: {
          id: "Jangan menunda pemeriksaan, karena kondisi dapat memburuk dengan cepat.",
          en: "Do not delay getting checked, because the condition can worsen quickly.",
        },
      },
      {
        tipe: "judul",
        teks: { id: "Kenapa Deteksi Dini Penting", en: "Why Early Detection Matters" },
      },
      {
        tipe: "paragraf",
        teks: {
          id: "Deteksi dini dan penanganan cepat sangat penting untuk mencegah komplikasi dan menyelamatkan nyawa ibu. Jika Anda hamil dan mengalami nyeri perut atau perdarahan, segera periksa ke fasilitas kesehatan.",
          en: "Early detection and prompt treatment are essential to prevent complications and save the mother's life. If you are pregnant and experience abdominal pain or bleeding, seek medical care immediately.",
        },
      },
    ],
    faq: [
      {
        tanya: {
          id: "Apakah kehamilan ektopik bisa berkembang menjadi kehamilan normal?",
          en: "Can an ectopic pregnancy develop into a normal pregnancy?",
        },
        jawab: {
          id: "Tidak. Karena sel telur yang dibuahi berkembang di luar rahim, kehamilan ini tidak dapat berlanjut secara normal dan memerlukan penanganan medis.",
          en: "No. Because the fertilised egg develops outside the womb, the pregnancy cannot continue normally and requires medical treatment.",
        },
      },
    ],
    referensi: [],
  },

  {
    slug: "mioma-uteri",
    tanggal: "2026-07-02",
    penulis: { id: "Tim RS Jakarta Medical Center", en: "Jakarta Medical Center Hospital Team" },
    ditinjau: { id: "", en: "" },
    judul: {
      id: "Mioma Uteri: Bukan Kanker, Tetapi Tetap Perlu Dipantau",
      en: "Uterine Fibroids: Not Cancer, But Still Worth Monitoring",
    },
    ringkas: {
      id: "Mioma adalah tumor jinak pada rahim. Tidak semuanya harus dioperasi, tetapi haid yang tidak normal sebaiknya tidak diabaikan.",
      en: "Fibroids are benign tumours in the womb. Not all of them need surgery, but abnormal periods should not be ignored.",
    },
    gambar: "/images/FASILITAS/3.jpg",
    gambarAlt: {
      id: "Model anatomi rahim yang memperlihatkan letak mioma, dipegang oleh tenaga medis.",
      en: "An anatomical model of a uterus showing where fibroids grow, held by a healthcare worker.",
    },
    materi: "mioma",
    isi: [
      {
        tipe: "judul",
        teks: { id: "Empat Hal yang Perlu Diketahui", en: "Four Things Worth Knowing" },
      },
      {
        tipe: "daftar",
        butir: [
          { id: "Mioma adalah tumor jinak dan bukan kanker", en: "Fibroids are benign tumours, not cancer" },
          { id: "Tidak semua mioma harus dioperasi", en: "Not all fibroids need surgery" },
          { id: "Jangan abaikan haid yang tidak normal", en: "Do not ignore abnormal periods" },
          {
            id: "Semakin cepat diperiksa, semakin mudah ditangani",
            en: "The sooner it is checked, the easier it is to treat",
          },
        ],
      },
      {
        tipe: "judul",
        teks: { id: "Bisakah Mioma Dicegah?", en: "Can Fibroids Be Prevented?" },
      },
      {
        tipe: "paragraf",
        teks: {
          id: "Tidak ada cara pasti untuk mencegah mioma, tetapi risikonya dapat dikurangi dengan:",
          en: "There is no certain way to prevent fibroids, but the risk can be reduced by:",
        },
      },
      {
        tipe: "daftar",
        butir: [
          { id: "Menjaga berat badan ideal", en: "Maintaining a healthy weight" },
          {
            id: "Pola makan sehat, tinggi serat, sayur, dan buah",
            en: "Eating a healthy diet high in fibre, vegetables, and fruit",
          },
          { id: "Olahraga teratur", en: "Exercising regularly" },
          { id: "Mengelola stres", en: "Managing stress" },
          { id: "Pemeriksaan kesehatan rutin", en: "Having regular health check-ups" },
        ],
      },
      {
        tipe: "paragraf",
        teks: {
          id: "Mioma bukan kanker, tetapi tetap perlu dipantau dan ditangani dengan tepat. Sayangi kesehatan reproduksi Anda - pemeriksaan dini dapat mencegah komplikasi dan menjaga kualitas hidup.",
          en: "Fibroids are not cancer, but they still need to be monitored and managed properly. Look after your reproductive health - early check-ups can prevent complications and protect your quality of life.",
        },
      },
    ],
    faq: [
      {
        tanya: {
          id: "Apakah semua mioma harus dioperasi?",
          en: "Do all fibroids need surgery?",
        },
        jawab: {
          id: "Tidak. Tidak semua mioma harus dioperasi. Pilihan penanganannya ditentukan dokter sesuai kondisi masing-masing pasien.",
          en: "No. Not all fibroids require surgery. The treatment option is decided by the doctor according to each patient's condition.",
        },
      },
    ],
    referensi: [],
  },

  {
    slug: "igd-atau-poli",
    tanggal: "2026-07-01",
    penulis: { id: "Tim RS Jakarta Medical Center", en: "Jakarta Medical Center Hospital Team" },
    ditinjau: { id: "", en: "" },
    judul: {
      id: "IGD atau Poli? Begini Cara Memilihnya",
      en: "Emergency Department or Outpatient Clinic? How to Choose",
    },
    ringkas: {
      id: "Tidak semua keluhan harus ke IGD, dan tidak semua bisa menunggu jadwal poli. Kenali bedanya agar penanganan Anda lebih cepat dan tepat.",
      en: "Not every complaint needs the emergency department, and not every one can wait for a clinic appointment. Knowing the difference gets you the right care faster.",
    },
    gambar: "/images/FASILITAS/4.jpg",
    gambarAlt: {
      id: "Kamar rawat inap RS JMC dengan ranjang pasien dan tirai pembatas antar tempat tidur.",
      en: "A JMC Hospital inpatient room with patient beds and privacy curtains between them.",
    },
    materi: "flyer-igd-poli",
    isi: [
      {
        tipe: "paragraf",
        teks: {
          id: "Saat tiba di rumah sakit, salah satu pertanyaan pertama yang muncul adalah ke mana harus menuju: Instalasi Gawat Darurat (IGD) atau poliklinik. Keduanya melayani pasien, tetapi dirancang untuk keperluan yang berbeda.",
          en: "One of the first questions when arriving at a hospital is where to go: the emergency department or the outpatient clinic. Both serve patients, but they are designed for different needs.",
        },
      },
      {
        tipe: "paragraf",
        teks: {
          id: "Tidak semua keluhan harus dibawa ke IGD, dan tidak semua keluhan bisa menunggu jadwal poli. Memilih layanan yang sesuai membantu Anda mendapatkan penanganan yang lebih cepat dan lebih tepat.",
          en: "Not every complaint needs to go to the emergency department, and not every complaint can wait for a clinic appointment. Choosing the right service helps you get faster and more appropriate care.",
        },
      },
      {
        tipe: "judul",
        teks: {
          id: "Apa Bedanya IGD dan Poli?",
          en: "What Is the Difference Between the Emergency Department and the Clinic?",
        },
      },
      {
        tipe: "paragraf",
        teks: {
          id: "IGD, atau Instalasi Gawat Darurat, melayani kondisi yang membutuhkan penanganan segera dan tidak dapat ditunda. Layanan ini terbuka sepanjang hari tanpa perlu membuat janji lebih dulu.",
          en: "The emergency department handles conditions that need immediate treatment and cannot be postponed. It is open around the clock, with no appointment needed.",
        },
      },
      {
        tipe: "paragraf",
        teks: {
          id: "Poli, atau poliklinik, melayani keluhan yang masih bisa dijadwalkan. Di sini Anda bertemu dokter sesuai jadwal praktiknya, dengan waktu konsultasi yang lebih leluasa.",
          en: "The outpatient clinic handles complaints that can still be scheduled. Here you see a doctor according to their practice hours, with more time for consultation.",
        },
      },
      {
        tipe: "judul",
        teks: {
          id: "Pilih IGD Jika Kondisinya Darurat",
          en: "Choose the Emergency Department If It Is Urgent",
        },
      },
      {
        tipe: "paragraf",
        teks: {
          id: "Beberapa contoh kondisi yang sebaiknya langsung dibawa ke IGD:",
          en: "Some examples of conditions that should go straight to the emergency department:",
        },
      },
      {
        tipe: "daftar",
        butir: [
          { id: "Sesak napas", en: "Shortness of breath" },
          { id: "Nyeri dada", en: "Chest pain" },
          { id: "Perdarahan", en: "Bleeding" },
          { id: "Kejang", en: "Seizures" },
          { id: "Kecelakaan", en: "Accidents" },
          { id: "Penurunan kesadaran", en: "Loss of consciousness" },
        ],
      },
      {
        tipe: "judul",
        teks: {
          id: "Pilih Poli Jika Keluhan Tidak Darurat",
          en: "Choose the Clinic If It Is Not Urgent",
        },
      },
      {
        tipe: "paragraf",
        teks: {
          id: "Poliklinik lebih sesuai untuk keperluan seperti:",
          en: "The outpatient clinic is more suitable for needs such as:",
        },
      },
      {
        tipe: "daftar",
        butir: [
          { id: "Kontrol rutin", en: "Routine follow-up" },
          { id: "Konsultasi kesehatan", en: "Health consultations" },
          { id: "Pemeriksaan", en: "Examinations" },
          {
            id: "Keluhan yang masih bisa dijadwalkan",
            en: "Complaints that can still be scheduled",
          },
        ],
      },
      {
        tipe: "bacaJuga",
        teks: {
          id: "Lihat jadwal praktik dokter RS JMC",
          en: "See the JMC Hospital doctor schedule",
        },
        href: "/jadwal-dokter",
      },
      {
        tipe: "judul",
        teks: {
          id: "Bagaimana Jika Anda Ragu?",
          en: "What If You Are Unsure?",
        },
      },
      {
        tipe: "paragraf",
        teks: {
          id: "Jika ragu menentukan tujuan layanan, jangan sungkan bertanya ke petugas informasi rumah sakit. Mereka dapat membantu mengarahkan Anda ke layanan yang sesuai dengan keluhan yang Anda alami.",
          en: "If you are unsure which service to go to, do not hesitate to ask the hospital information desk. They can help direct you to the service that fits your complaint.",
        },
      },
      {
        tipe: "paragraf",
        teks: {
          id: "RS Jakarta Medical Center siap melayani dengan tepat untuk kebutuhan kesehatan Anda, baik melalui IGD yang buka 24 jam maupun poliklinik sesuai jadwal dokter.",
          en: "Jakarta Medical Center Hospital is ready to serve your health needs appropriately, whether through the 24-hour emergency department or the outpatient clinic according to the doctor's schedule.",
        },
      },
    ],
    faq: [
      {
        tanya: {
          id: "Apakah ke IGD harus membuat janji lebih dulu?",
          en: "Do I need an appointment to go to the emergency department?",
        },
        jawab: {
          id: "Tidak. IGD melayani kondisi yang membutuhkan penanganan segera dan terbuka tanpa perlu membuat janji lebih dulu.",
          en: "No. The emergency department handles conditions needing immediate treatment and is open without a prior appointment.",
        },
      },
      {
        tanya: {
          id: "Kalau keluhan saya ringan tetapi terasa mengganggu, sebaiknya ke mana?",
          en: "If my complaint is mild but bothersome, where should I go?",
        },
        jawab: {
          id: "Keluhan yang masih bisa dijadwalkan lebih sesuai ditangani di poliklinik. Di sana waktu konsultasi lebih leluasa dan Anda bertemu dokter sesuai jadwal praktiknya.",
          en: "Complaints that can still be scheduled are better handled at the outpatient clinic, where there is more time for consultation and you see the doctor at their scheduled hours.",
        },
      },
      {
        tanya: {
          id: "Bagaimana jika saya tidak yakin keluhan saya darurat atau tidak?",
          en: "What if I am not sure whether my complaint is an emergency?",
        },
        jawab: {
          id: "Tanyakan ke petugas informasi rumah sakit. Mereka dapat membantu mengarahkan Anda ke layanan yang sesuai.",
          en: "Ask the hospital information desk. They can help direct you to the appropriate service.",
        },
      },
    ],
    referensi: [],
  },

  /* ============================================================
     ARTIKEL BERIKUTNYA

     Salin blok di atas, ganti isinya. Yang paling praktis: ambil satu
     materi dari data.js, ketik ulang isinya, lalu isi `materi` dengan
     id materi itu supaya artikel dan versi cetaknya saling menunjuk.

     Kandidat yang paling berguna didahulukan - ini materi yang paling
     sering dicari orang lewat mesin pencari:
       stroke, ketoasidosis, gerd, diare, kejang-demam
     ============================================================ */
];

/* Diurutkan terbaru dulu. Dipakai halaman artikel untuk daftar
   "Artikel Terkait", dan nanti oleh halaman daftar artikel. */
export const ARTIKEL_TERBARU = [...ARTIKEL].sort(
  (a, b) => new Date(b.tanggal) - new Date(a.tanggal)
);

export function cariArtikel(slug) {
  return ARTIKEL.find((a) => a.slug === slug) ?? null;
}
